import { PLAYER_SPEED_TILES_PER_S } from '@etown/shared';

/** Wegpunkt gilt als erreicht, sobald der Avatar so nah dran ist (Tiles). */
const WAYPOINT_EPS = 0.15;
/** Kein Fortschritt fuer so lange -> Auto-Walk abbrechen (Tuer zu, Korrektur). */
const STUCK_TIMEOUT_MS = 500;
/** Fortschritt < diesem Wert gilt als "kein Fortschritt". */
const PROGRESS_EPS = 0.01;

/**
 * Fuehrt den Avatar entlang einer Wegpunktliste — erzeugt pro Frame einen
 * Eingabevektor, den LocalPlayer.update wie Tastatur-Eingabe konsumiert.
 * Dadurch gelten Speed-Limit, Wall-Slide, Zutrittssperren und der 15-Hz-
 * player:move-Strom unveraendert; hier liegt nur das "wohin".
 */
export class AutoWalk {
  private waypoints: Array<{ x: number; y: number }> = [];
  private index = 0;
  private bestDist = Number.POSITIVE_INFINITY;
  private stuckMs = 0;

  get active(): boolean {
    return this.index < this.waypoints.length;
  }

  /** Neue Route setzen (Welt-Koordinaten, Tile-Mittelpunkte). */
  start(waypoints: ReadonlyArray<{ x: number; y: number }>): void {
    this.waypoints = waypoints.map((w) => ({ x: w.x, y: w.y }));
    this.index = 0;
    this.bestDist = Number.POSITIVE_INFINITY;
    this.stuckMs = 0;
  }

  cancel(): void {
    this.waypoints = [];
    this.index = 0;
  }

  /**
   * Eingabevektor Richtung naechster Wegpunkt; {0,0} wenn inaktiv/angekommen.
   * Nahe am Wegpunkt wird die Magnitude gedrosselt (exakte Ankunft ohne
   * Ueberschwingen — LocalPlayer skaliert den Schritt mit der Vektorlaenge).
   */
  vector(dtMs: number, pos: { x: number; y: number }): { x: number; y: number } {
    if (!this.active) return { x: 0, y: 0 };
    let target = this.waypoints[this.index]!;
    let dx = target.x - pos.x;
    let dy = target.y - pos.y;
    let dist = Math.hypot(dx, dy);

    // Wegpunkt erreicht -> zum naechsten weiterruecken
    while (dist <= WAYPOINT_EPS && this.index < this.waypoints.length - 1) {
      this.index += 1;
      this.bestDist = Number.POSITIVE_INFINITY;
      this.stuckMs = 0;
      target = this.waypoints[this.index]!;
      dx = target.x - pos.x;
      dy = target.y - pos.y;
      dist = Math.hypot(dx, dy);
    }

    // Letzter Wegpunkt erreicht -> fertig
    if (dist <= WAYPOINT_EPS) {
      this.cancel();
      return { x: 0, y: 0 };
    }

    // Stuck-Erkennung: kein Naeherkommen ueber STUCK_TIMEOUT_MS -> abbrechen
    if (dist < this.bestDist - PROGRESS_EPS) {
      this.bestDist = dist;
      this.stuckMs = 0;
    } else {
      this.stuckMs += dtMs;
      if (this.stuckMs >= STUCK_TIMEOUT_MS) {
        this.cancel();
        return { x: 0, y: 0 };
      }
    }

    // Normierter Richtungsvektor, nahe am Ziel gedrosselt
    const maxStep = PLAYER_SPEED_TILES_PER_S * (dtMs / 1000);
    const scale = Math.min(1, dist / Math.max(maxStep, 1e-6)) / dist;
    return { x: dx * scale, y: dy * scale };
  }
}
